import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useGetConversationThreadQuery,
  useReplyToMessageMutation,
  useMarkAsReadMutation,
} from 'store/services/message/messageApi';
import { useMessageSocket } from 'hooks/useMessageSocket';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { isSystemMessage, isSystemConversation } from 'utils/messageHelpers';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import type { Message, MessageWithReadReceipt } from 'types/interfaces/message';
import './ConversationThreadPage.css';

// Convert literal bullet characters (•) to proper Markdown list syntax
const convertBulletsToMarkdown = (content: string): string => {
  // Add newlines before standalone bullets that follow text
  let processed = content.replace(/([^\n])\s*•\s+/g, '$1\n- ');
  // Handle bullets at the very start of the string
  processed = processed.replace(/^•\s+/, '- ');
  return processed;
};

export default function ConversationThreadPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const [replyMessage, setReplyMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const previousMessageCountRef = useRef<number>(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useGetConversationThreadQuery(conversationId!);
  const [replyToMessage, { isLoading: isReplying }] = useReplyToMessageMutation();
  const [markAsRead] = useMarkAsReadMutation();

  // Get socket for emitting mark as read
  const { emitMarkAsRead } = useMessageSocket({ enabled: true });

  // Mark as read immediately when conversation loads
  useEffect(() => {
    if (conversationId && data) {
      // Call REST API for persistence
      markAsRead(conversationId);

      // Also emit via WebSocket for real-time read receipts to sender
      const unreadMessageIds = data.messages
        .filter((m: Message) => !m.isRead && m.sender?.id === data.otherUser.id)
        .map((m: Message) => m.id);

      if (unreadMessageIds.length > 0) {
        emitMarkAsRead(conversationId, unreadMessageIds);
      }
    }
  }, [conversationId, data, markAsRead, emitMarkAsRead]);

  // Auto-scroll chat container to bottom on initial load and when new messages arrive
  // Uses scrollTop instead of scrollIntoView to avoid scrolling the entire page
  useEffect(() => {
    if (data?.messages && messagesContainerRef.current) {
      const currentMessageCount = data.messages.length;

      // Scroll on initial load (previousCount === 0) or when new message added
      if (previousMessageCountRef.current === 0 || currentMessageCount > previousMessageCountRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }

      previousMessageCountRef.current = currentMessageCount;
    }
  }, [data?.messages]);


  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = replyMessage.substring(0, start) + emoji + replyMessage.substring(end);

    setReplyMessage(newMessage);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !conversationId) return;

    try {
      await replyToMessage({
        conversationId,
        content: replyMessage.trim(),
      }).unwrap();

      setReplyMessage('');
      toast.success('Reply sent!');
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      // 429 errors are now handled globally in baseQuery
      // Only handle other errors here
      if (error.status !== 429) {
        toast.error(error.data?.message || 'Failed to send reply');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="conversation-page">
        <div className="conversation-loading">Loading conversation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-page">
        <div className="conversation-error">
          {(error as any).status === 404
            ? "This conversation doesn't exist or you don't have access."
            : 'Failed to load conversation. Please try again.'}
        </div>
        <button onClick={() => navigate('/inbox')} className="back-to-inbox-button">
          Back to Inbox
        </button>
      </div>
    );
  }

  if (!data) return null;

  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isSystemThread = isSystemConversation(data.otherUser.id, data.otherUser.username);

  return (
    <div className="conversation-page">
      <div className="conversation-header">
        <button onClick={() => navigate('/inbox')} className="back-button">
          ← Back to Inbox
        </button>
        <div className="conversation-user-info">
          <AvatarWithFlag
            avatarUrl={data.otherUser.avatarUrl}
            username={data.otherUser.username}
            countryCode={data.otherUser.country}
            size={40}
          />
          <span className="conversation-username">{data.otherUser.username}</span>
        </div>
      </div>

      <div className="conversation-messages" ref={messagesContainerRef}>
        {data.messages.map((message) => {
          const isSystem = isSystemMessage(message);
          return (
            <div
              key={message.id}
              className={`message-bubble ${
                isSystem
                  ? 'system-message'
                  : message.sender?.id === data.otherUser.id ? 'received' : 'sent'
              }`}
            >
              <div className="message-sender-info">
                {isSystem ? (
                  <div className="message-avatar-placeholder system-avatar">S</div>
                ) : (
                  <AvatarWithFlag
                    avatarUrl={message.sender?.avatarUrl}
                    username={message.sender?.username}
                    countryCode={message.sender?.country}
                    size={24}
                  />
                )}
                <span className="message-sender">{message.sender?.username || 'System'}</span>
                <span className="message-time">{formatMessageTime(message.createdAt)}</span>
              </div>
              <div className="message-content">
                {isSystem ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => {
                        const isExternal = href?.startsWith('http');
                        const isMailto = href?.startsWith('mailto:');

                        if (isMailto) {
                          return <a href={href}>{children}</a>;
                        }

                        if (isExternal) {
                          return (
                            <a href={href} target="_blank" rel="noopener noreferrer">
                              {children}
                            </a>
                          );
                        }

                        return <Link to={href || '#'}>{children}</Link>;
                      },
                    }}
                  >
                    {convertBulletsToMarkdown(message.content)}
                  </ReactMarkdown>
                ) : (
                  message.content
                )}
              </div>
              {/* Read receipt for sent messages */}
              {!isSystem && message.sender?.id !== data.otherUser.id && (message as MessageWithReadReceipt).readAt && (
                <div className="message-read-receipt">
                  <span className="read-check">✓</span>
                  <span>Read {formatRelativeTime((message as MessageWithReadReceipt).readAt!)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isSystemThread && (
        <div className="conversation-reply-section">
          <div className="reply-form-group">
            <label className="reply-label">Reply</label>
            <textarea
              ref={textareaRef}
              className="reply-textarea"
              placeholder="Type your reply here..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              rows={4}
              maxLength={2000}
            />
            <CharacterCounter
              currentLength={replyMessage.length}
              maxLength={2000}
              warningThreshold={1800}
            />
          </div>

          <div className="reply-actions">
            <div className="emoji-picker-container">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                😀
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                  buttonRef={emojiButtonRef}
                />
              )}
            </div>

            <MagicButton
              label={isReplying ? 'Sending...' : 'Send Reply'}
              color="purple"
              onPress={handleReply}
              isDisabled={
                !replyMessage.trim() ||
                replyMessage.length > 2000 ||
                isReplying
              }
              fullWidth
            />
          </div>
        </div>
      )}
    </div>
  );
}
